import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import { API } from "@/lib/api";
import { WhatsAppCTA } from "@/components/WhatsApp";
import { DEFAULT_WA_MESSAGES, WA_LABELS } from "@/config/site";
import { useAuth } from "@/lib/auth";
import { Quote, Star, ArrowRight, TrendingUp, HeartPulse, Users } from "lucide-react";
import { usePageCms } from "@/lib/siteSettings";

const STATS = [
  { icon: HeartPulse, k: "Sports rehabilitation", v: "Hands-on & remote care" },
  { icon: TrendingUp, k: "Hybrid care", v: "Clinic, gym & online" },
  { icon: Users,      k: "Objective testing",   v: "Measured outcomes" },
];

export default function Testimonials() {
  const { t: tr } = useTranslation();
  const { user } = useAuth();
  const cms = usePageCms("testimonials");
  const bookHref = user ? "/app/appointments" : `/register?redirect=${encodeURIComponent("/app/appointments")}`;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios.get(`${API}/testimonials/published`)
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);
  return (
    <div className="min-h-screen fl-noise fl-page" style={{ background: "var(--fl-bg)", color: "var(--fl-text)" }}>
      <PublicHeader />

      {/* Hero */}
      {cms.isVisible("hero") && (
      <section className="pt-32 md:pt-40 pb-20 md:pb-24 px-6 bg-[#0A0A0A] fl-noise relative overflow-hidden">
        <div className="max-w-5xl mx-auto text-center relative">
          <div className="fl-label text-[color:var(--fl-orange)] mb-4">{cms.text("hero", "eyebrow", tr("testimonialsPage.eyebrow"))}</div>
          <h1 className="fl-heading text-5xl md:text-6xl leading-none">
            {cms.get("hero").heading ? cms.get("hero").heading : (<>{tr("testimonialsPage.h1a")}<br /><span className="text-[color:var(--fl-orange)]">{tr("testimonialsPage.h1b")}</span></>)}
          </h1>
          <p className="mt-6 text-lg max-w-2xl mx-auto" style={{ color: "var(--fl-muted)" }}>
            {cms.text("hero", "body", tr("testimonialsPage.subtitle"))}
          </p>
        </div>

        <div className="max-w-4xl mx-auto grid grid-cols-3 gap-4 mt-12 fl-stagger relative">
          {STATS.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.v} className="fl-card p-6 text-center">
                <Icon size={20} className="text-[color:var(--fl-orange)] mx-auto" />
              <div className="fl-heading text-3xl md:text-4xl mt-3 text-[color:var(--fl-orange)]">{s.k}</div>
                <div className="fl-label mt-1">{s.v}</div>
              </div>
            );
          })}
        </div>
      </section>
      )}

      {/* Testimonials grid */}
      <section className="py-24 md:py-32 px-6 bg-[#121212] border-t border-white/5">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-5 fl-stagger">
          {loading && (
            <div className="fl-card p-8 md:col-span-2 text-center text-sm" style={{ color: "var(--fl-muted)" }}>
              Loading testimonials…
            </div>
          )}
          {!loading && items.length === 0 && (
            <div className="fl-card p-8 md:col-span-2 text-center text-sm" style={{ color: "var(--fl-muted)" }}>
              Be the first to share your story after your next session.
            </div>
          )}
          {items.map((t) => (
            <article
              key={t.id || t.name}
              className="fl-card p-8 relative overflow-hidden"
              data-testid={`testimonial-${(t.name || "").split(" ")[0].toLowerCase()}`}
            >
              <Quote size={28} className="text-[color:var(--fl-orange)] opacity-30 absolute top-6 right-6" />
              <div className="flex gap-1 mb-4">
                {Array.from({ length: t.rating || 5 }).map((_, i) => (
                  <Star key={i} size={14} className="text-[color:var(--fl-orange)] fill-[color:var(--fl-orange)]" />
                ))}
              </div>
              <p className="text-base leading-relaxed" style={{ color: "var(--fl-text)" }}>
                &ldquo;{t.quote}&rdquo;
              </p>
              <div className="mt-6 pt-6 border-t" style={{ borderColor: "var(--fl-border)" }}>
                <div className="fl-heading text-lg">{t.name}</div>
                {(t.context || t.clinician_name) && (
                  <div className="text-xs mt-1" style={{ color: "var(--fl-muted-2)" }}>
                    {[t.context, t.clinician_name && `Treated by ${t.clinician_name}`].filter(Boolean).join(" · ")}
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Trust strip */}
      {cms.isVisible("trust") && (
      <section className="py-20 md:py-28 px-6 bg-[#0A0A0A] border-t border-white/5">
        <div className="max-w-6xl mx-auto text-center">
          <div className="fl-label mb-3">{cms.text("trust", "heading", "Trusted by")}</div>
          <div className="text-sm mt-2" style={{ color: "var(--fl-muted)" }}>
            Amateur athletes · Weekend warriors · Club teams · Post-op patients · Corporate wellbeing programmes
          </div>
        </div>
      </section>
      )}

      {/* CTA */}
      {cms.isVisible("cta") && (
      <section className="py-24 md:py-32 px-6 bg-gradient-to-b from-[#1A1A1A] via-[#170f0d] to-[#0A0A0A] border-t border-white/5">
        <div className="max-w-4xl mx-auto fl-card p-10 text-center relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at 50% 0%, rgba(255,69,0,0.10), transparent 60%)" }} />
          <div className="relative">
            <h3 className="fl-heading text-3xl md:text-4xl">{cms.text("cta", "heading", tr("testimonialsPage.ctaTitle"))}</h3>
            <p className="mt-3 max-w-xl mx-auto text-sm" style={{ color: "var(--fl-muted)" }}>
              {cms.text("cta", "body", tr("testimonialsPage.ctaBody"))}
            </p>
            <Link to={bookHref} className="fl-btn text-base mt-6" data-testid="testimonials-cta-book">
              {cms.text("cta", "ctaLabel", tr("testimonialsPage.ctaBook"))} <ArrowRight size={16} />
            </Link>
            <div className="mt-4">
              <WhatsAppCTA message={DEFAULT_WA_MESSAGES.rehab} variant="soft" testid="testimonials-cta-wa">
                {tr("pub.wa.needHelp")}
              </WhatsAppCTA>
            </div>
          </div>
        </div>
      </section>
      )}

      <PublicFooter />
    </div>
  );
}
